<template>
  <popup-dialog
    class="chart-image-export-popup"
    :width="1000"
    :title="$t('headings.exportEvaluationImage')"
    :backdrop="true"
    :close-button-visible="true"
    :resizable="true"
    :resizable-edges="{ left: true, right: true, bottom: true, top: false }"
  >
    <div class="chart-image-export-popup__main">
      <div class="chart-image-export-popup__body evaluation-chart">
        <svg
          v-if="collectedChartData.data.length > 0"
          id="chart-image-export"
          ref="svgElement"
          :width="settings.width"
          :height="settings.height"
          :viewBox="`0 0 ${settings.width} ${settings.height}`"
          class="chart-image-export-popup__image"
        >
          <rect
            :width="settings.width"
            :height="settings.height"
            fill="var(--color-background-deep)"
          />

          <g v-if="titleVisible" ref="headerElement"></g>

          <component
            v-if="chartConfig"
            :is="chartConfig.component"
            ref="chartRef"
            class="evaluation-chart__chart chart-image-export-popup__chart"
            :class="{
              '-small': chartWidth < 400 || chartHeight < 400
            }"
            :data="collectedChartData.data"
            :column-data="collectedChartData.columns"
            :settings="{ ...settings, titleVisible: false }"
            :width="chartWidth"
            :height="chartHeight"
            :formatted-title="formattedTitle"
            :x="legendPosition == 'right' ? 0 : legendWidth + 10"
            :y="titleVisible ? titleHeight : 0"
            :animations-disabled="true"
            :title-clickable="false"
          />

          <chart-legend
            v-if="legendVisible"
            :id="`evaluation_${evaluationId}-legend`"
            ref="legendElement"
            :legend="collectedChartData.legend"
            :settings="settings"
            :x="legendPosition == 'left' ? 20 : chartWidth + 10"
            :y="(titleVisible ? titleHeight : 0) + 20"
          />

          <text
            class="evaluation-chart__notice"
            text-anchor="end"
            :x="settings.width - 8"
            :y="settings.height - 8"
          >
            {{ $t("texts.madeInSimcision") }}
          </text>
        </svg>
        <div v-else class="message">
          {{ $t("errors.noEvaluationData") }}
        </div>
      </div>
      <div class="chart-image-export-popup__sidebar">
        <chart-settings-list
          :chart="evaluation"
          :settings="settings"
          :body-width="100"
          :body-height="100"
          :fixed-size="true"
          :remove-settings="['width', 'height']"
          @update="updateSettings"
        >
          <input-checkbox
            v-model:value="titleVisible"
            :label="$t('labels.titleVisible')"
          />
          <input-checkbox
            v-model:value="legendVisible"
            :label="$t('labels.legendVisible')"
          />

          <div v-if="legendVisible" style="padding-left: 2.25rem">
            <div class="label">{{ $t("labels.legendPosition") }}</div>
            <input-radio
              v-model:value="legendPosition"
              data="left"
              :label="$t('labels.left')"
            />
            <input-radio
              v-model:value="legendPosition"
              data="right"
              :label="$t('labels.right')"
            />
          </div>
        </chart-settings-list>

        <div class="column mt-4">
          <div class="line">
            <div class="line">
              <input-number
                :label="$t('labels.width')"
                :value="settings.width ?? 0"
                :max="10000"
                :min="minWidth"
                @update:value="updateSetting('width', $event)"
              />
              <span class="label self-end pb-2" style="width: 20px">px</span>
            </div>
            <div class="line">
              <input-number
                :label="$t('labels.height')"
                :value="settings.height ?? 0"
                :max="10000"
                :min="minHeight"
                @update:value="updateSetting('height', $event)"
              />
              <span class="label self-end pb-2" style="width: 20px">px</span>
            </div>
          </div>

          <div class="mt-2">
            <div class="label">{{ $t("labels.imageFormat") }}</div>
            <div class="line gap-4">
              <input-radio
                v-model:value="imageFormat"
                data="image/svg"
                :label="$t('labels.svg')"
              />
              <input-radio
                v-model:value="imageFormat"
                data="image/png"
                class="mt-0"
                :label="$t('labels.png')"
              />
            </div>
          </div>

          <button
            type="button"
            class="button mt-2"
            :disabled="downloading || collectedChartData.data.length == 0"
            @click="downloadImage()"
          >
            <template v-if="downloading">
              {{ $t("messages.creatingImage") }}
            </template>
            <template v-else>
              {{ $t("actions.downloadImage") }}
            </template>
          </button>
        </div>
      </div>
    </div>
  </popup-dialog>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watchEffect } from "vue";
import { toRefs } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { downloadURL } from "@/helpers/data/Download";
import { svgToDataURL } from "@/helpers/utility/SVG";
import { drawEvaluationTitle } from "@editor/items/charts/chartDrawFunction";
import { mapChartData } from "../charts/chartDataMapper";
import { useChartTitle } from "../charts/useChartTitle";

import InputRadio from "@core/input/RadioButton.vue";
import PopupDialog from "@core/ui/PopupDialog.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";

import ChartLegend from "@editor/items/charts/ChartLegend.vue";
import ChartSettingsList from "@editor/items/charts/ChartSettingsList.vue";

import { useSceneTree } from "../scene/sceneTree";
import { useElements } from "../element/elements";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useRelationStore } from "../relation/relations.store";
import { useChartData } from "../charts/useChartData";
import { useEvaluationStore } from "./evaluations.store";
import { useSettingsStore } from "@editor/store/settings.store";

import type { ChartSettings } from "@editor/items/charts/ChartItem";

interface Props {
  evaluationId: number;
}
const props = defineProps<Props>();

const { t } = useI18n();
const evaluationStore = useEvaluationStore();

// DATA

const { legendVisible, legendPosition, imageFormat } = toRefs(
  reactive(evaluationStore.imageExportSettings)
);
const downloading = ref(false);

// elements
const legendElement = ref<InstanceType<typeof ChartLegend>>();
const svgElement = ref<SVGSVGElement>();
const headerElement = ref<SVGGElement>();

const evaluation = computed(() => {
  return evaluationStore.items[props.evaluationId];
});

// const ctx = useReactiveChartData(evaluation);

const {
  availableSettingsValues,
  chartConfig,
  chartAxisConfig,
  itemConfigs,
  dataToggles,
  selectedItem,
  selectedItems,
  selectedSimulation,
  selectedSimulations,
  simulationRounds,
  playedRoundsInRange,
  roundsInRange,
  commonRoundSettings,
  orderedTargets,
  orderedResources,
  orderedIndices,
  availableItems,
  allAvailableTargets,
  allAvailableResources,
  allAvailableIndices,
  allTargetsOrder,
  allResourcesOrder,
  allIndicesOrder,
  actionActivations,
  incidentActivations,
  scheduledEffectActivations,
} = useChartData(evaluation);

// ====================
// CHART DATA

const chartUid = computed(() => evaluation.value?.uid);
const chartType = computed(() => evaluation.value?.chartType ?? "bar");
const elementColors = computed(() => useSceneTree().elementColors);
const elementUnits = computed(() => useElements().units);
const titleAbbreviations = computed(() => useEvaluationStore().titleAbbreviations);
const activeSimulationId = computed(() => useSimulationViewStore().simulation?.id ?? null);
const outgoingRelationsByElement = computed(() => useRelationStore().activeByStartUid);

const collectedChartData = computed(() => {
  if (!chartConfig.value) return { data: [], legend: {} };
  const settings = availableSettingsValues.value;
  const chartData = mapChartData(
    {
      x: settings.xAxis,
      y: settings.yAxis
    },
    {
      chartUid: chartUid.value,
      chartType: chartType.value,
      itemConfigs: itemConfigs.value,
      dataToggles: dataToggles.value,

      selectedItem: selectedItem.value,
      selectedItems: selectedItems.value,
      selectedSimulation: selectedSimulation.value,
      selectedSimulations: selectedSimulations.value,

      availableSettingsValues: availableSettingsValues.value,
      chartAxisConfig: chartAxisConfig.value,
      commonRoundSettings: commonRoundSettings.value,

      simulationRounds: simulationRounds.value,
      playedRoundsInRange: playedRoundsInRange.value,
      roundsInRange: roundsInRange.value,

      orderedTargets: orderedTargets.value,
      orderedResources: orderedResources.value,
      orderedIndices: orderedIndices.value,
      availableItems: availableItems.value,
      allAvailableTargets: allAvailableTargets.value,
      allAvailableResources: allAvailableResources.value,
      allAvailableIndices: allAvailableIndices.value,
      allTargetsOrder: allTargetsOrder.value,
      allResourcesOrder: allResourcesOrder.value,
      allIndicesOrder: allIndicesOrder.value,

      actionActivations: actionActivations.value,
      incidentActivations: incidentActivations.value,
      scheduledEffectActivations: scheduledEffectActivations.value,

      elementColors: elementColors.value,
      elementUnits: elementUnits.value,
      outgoingRelationsByElement: outgoingRelationsByElement.value,
      titleAbbreviations: titleAbbreviations.value,

      activeSimulationId: activeSimulationId.value,
    },
    t
  );
  return chartData;
});

// SETTINGS

const scaleColors = computed(() => {
  const colors = useSettingsStore().targetValueColors.slice();
  if (colors == null) return [];
  return colors;
});

const overrideSettings = ref<
  Partial<ChartSettings> & { width: number; height: number }
>({
  width: 1400,
  height: 600
});

const settings = computed(() => {
  return {
    ...availableSettingsValues.value,
    scaleColors: scaleColors.value,
    ...overrideSettings.value
  };
});

const titleVisible = computed({
  get() {
    return settings.value.titleVisible ?? false;
  },
  set(value: boolean) {
    updateSetting("titleVisible", value);
  }
});

const { formattedTitle, title } = useChartTitle(() => evaluation.value?.title ?? "", () => ({
  singleSimulation: chartAxisConfig.value?.singleSimulation,
  selectedSimulations: selectedSimulations.value,
  selectedItems: selectedItems.value,
  availableItems: availableItems.value,
  dataToggles: dataToggles.value
}));

// Generate the title if the header element is available:
watchEffect(() => {
  if (headerElement.value == null) return;

  drawEvaluationTitle(
    headerElement.value,
    settings.value.width,
    formattedTitle.value
  );
});

const legendWidth = computed(() => {
  return legendElement.value?.totalWidth || 0;
});
const legendHeight = computed(() => {
  return (legendElement.value?.totalHeight || 0) + 40;
});
const titleHeight = computed(() => {
  if (titleVisible.value) return 45;
  return 0;
});

const minWidth = computed(() => {
  return Math.floor(Math.max(legendWidth.value + 300, 300));
});
const minHeight = computed(() => {
  return Math.floor(Math.max(legendHeight.value + titleHeight.value, 300));
});

const chartWidth = computed(() => {
  let width = settings.value.width;

  if (legendVisible.value) {
    width -= legendWidth.value;
    width -= 20; // gap
  }

  return width;
});
const chartHeight = computed(() => {
  let height = settings.value.height - titleHeight.value;
  return height;
});

// FUNCTIONS

function updateSetting<K extends keyof ChartSettings>(
  key: K,
  value: (ChartSettings & { width: number; height: number })[K]
) {
  overrideSettings.value[key] = value;
}
function updateSettings(data: Partial<ChartSettings>) {
  Object.assign(overrideSettings.value, data);
}

function getSVGAsDataURL() {
  const srcSvgElement = svgElement.value || null;
  srcSvgElement?.classList.add("-print");
  const url = svgToDataURL(srcSvgElement);
  srcSvgElement?.classList.remove("-print");

  return url;
}

function downloadImage() {
  downloading.value = true;

  setTimeout(() => {
    const svgDataURL = getSVGAsDataURL();
    if (!svgDataURL) {
      throw new Error("There is no image to download.");
    }

    if (imageFormat.value == "image/svg") {
      downloadURL(title.value, svgDataURL);
    } else {
      const width = settings.value.width;
      const height = settings.value.height;

      const canvasElement = document.createElement("canvas");
      canvasElement.width = width;
      canvasElement.height = height;

      const image = new Image();
      image.src = svgDataURL;
      image.width = width;
      image.height = height;

      const context = canvasElement.getContext("2d");
      image.addEventListener("load", () => {
        context?.drawImage(image, 0, 0, width, height, 0, 0, width, height);
        const dataURL = canvasElement.toDataURL(imageFormat.value);
        downloadURL(title.value, dataURL);
      });
    }

    downloading.value = false;
  }, 10);
}
</script>

<style lang="scss">
.chart-image-export-popup {
  padding: 5px;
  padding-top: 0;
  padding-left: 1rem;
  padding-bottom: 1rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: min-content minmax(0, 1fr);
  gap: 1rem;

  &__main {
    display: grid;
    gap: 1rem;
    grid-template-columns: minmax(0, 1fr) 18rem;
    overflow: hidden;
  }
  &__body {
    overflow: auto;
    max-height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    // background-color: var(--color-shadow-1);
  }
  &__chart {
    outline: none !important;
  }
  &__image {
    display: block;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    outline: 1px solid var(--color-grey-light);
    outline-offset: -1px;
    font-family: var(--font-family-ui);
  }
  &__sidebar {
    padding: 0.5rem;
    box-shadow: 0 0 10px var(--color-shadow-1);
    overflow: auto;
  }
}
</style>
